// VPlayer.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";

// VPlayer.jsx - Updated version
export const VPlayer = ({ source, isVisible }) => {
  const videoNode = useRef(null);
  const playerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const isInitializedRef = useRef(false);

  const initializePlayer = useCallback(() => {
    if (!videoNode.current || playerRef.current || isInitializedRef.current) return;

    isInitializedRef.current = true;
    playerRef.current = videojs(
      videoNode.current,
      {
        controls: true,
        autoplay: false, // Changed to false to prevent autoplay issues
        muted: true, // Start muted for better compatibility
        preload: "metadata", // Changed to metadata for faster loading
        fluid: false, // Changed to false for better control
        responsive: true,
        aspectRatio: "16:9", // Added aspect ratio
        width: "100%",
        height: 400,
        sources: [source],
      },
      () => {
        setPlayerReady(true);
        console.log("player is ready");
      }
    );

    // Add error handling
    playerRef.current.on('error', (error) => {
      console.error('Video.js error:', error);
    });

    addKeyboardControls();
  }, [source]);

  const addKeyboardControls = () => {
    const handlePlayPause = () => {
      if (!playerRef.current) return;
      playerRef.current.paused() ? playerRef.current.play() : playerRef.current.pause();
    };

    const handleKeyboardEvent = (event) => {
      if (event.key === "Enter" || event.key === "GamepadA") {
        handlePlayPause();
      }
    };

    window.addEventListener("keydown", handleKeyboardEvent);
    playerRef.current.keyboardCleanup = () => {
      window.removeEventListener("keydown", handleKeyboardEvent);
    };
  };

  useEffect(() => {
    if (isVisible && videoNode.current) {
      const timer = setTimeout(initializePlayer, 100); // Increased delay
      return () => clearTimeout(timer);
    }
  }, [isVisible, initializePlayer]);

  useEffect(() => {
    if (playerRef.current && playerReady && source) {
      playerRef.current.src(source);
    }
  }, [source, playerReady]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.keyboardCleanup?.();
        playerRef.current.dispose();
        playerRef.current = null;
        isInitializedRef.current = false;
        setPlayerReady(false);
      }
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div 
      data-vjs-player 
      style={{ 
        width: "100%", 
        height: "400px",
        minHeight: "400px", // Added minimum height
        backgroundColor: "#000" // Added background color
      }}
    >
      <video
        ref={videoNode}
        className="video-js vjs-default-skin vjs-big-play-centered"
        playsInline
        style={{ 
          width: "100%", 
          height: "100%",
          minHeight: "400px" // Added minimum height
        }}
      />
    </div>
  );
};
